import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Collapse,
  alpha,
  GlobalStyles 
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SupportFAQ from '../components/SupportFAQ';
import CopyrightClaimForm from '../components/CopyrightClaimForm';
import DeleteDataForm from '../components/DeleteDataForm';
import ContactSupportForm from '../components/ContactSupportForm';

const SupportPage: React.FC = () => {
  const theme = useTheme();
  const [showCopyrightForm, setShowCopyrightForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);

  const handleCopyrightClick = () => {
    setShowCopyrightForm(!showCopyrightForm);
    setShowDeleteForm(false);
    setShowContactForm(false);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('copyright-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDeleteClick = () => {
    setShowDeleteForm(!showDeleteForm);
    setShowCopyrightForm(false);
    setShowContactForm(false);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('delete-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleContactClick = () => {
    setShowContactForm(!showContactForm);
    setShowCopyrightForm(false);
    setShowDeleteForm(false);
    // Scroll to form
    setTimeout(() => {
      document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <>
      {/* Add global styles for font */}
      <GlobalStyles
        styles={{
          '@import': 'url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap")',
          'body, .MuiTypography-root, .MuiButton-root, .MuiInputBase-root': {
            fontFamily: '"Poppins", sans-serif !important'
          },
          'input, select, textarea, label, .MuiInputLabel-root, .MuiMenuItem-root, .MuiChip-label': {
            fontFamily: '"Poppins", sans-serif !important'
          }
        }}
      />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #d10046 0%, #2a0541 100%)',
          minHeight: '100vh',
          color: '#fff',
          py: { xs: 4, md: 6 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Background gradient overlay with subtle animation */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at bottom right, rgba(209,0,70,0.3), transparent 70%)',
            animation: 'pulse 8s infinite ease-in-out',
            '@keyframes pulse': {
              '0%': { opacity: 0.7 },
              '50%': { opacity: 0.9 },
              '100%': { opacity: 0.7 },
            },
            zIndex: 0,
          }}
        />

        {/* Main content container */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              How Can We Help?
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '700px', mx: 'auto' }}>
              We're here to support you. Find answers, report issues, or get in touch with our team.
            </Typography>
          </Box>

          {/* Quick Links Section */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: alpha('#000', 0.4),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(228, 255, 26, 0.2)',
                    borderColor: '#E4FF1A',
                  }
                }}
                onClick={() => document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <CardContent sx={{ py: 4 }}>
                  <HelpOutlineIcon sx={{ fontSize: 48, color: '#E4FF1A', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    FAQ
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Find quick answers to common questions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: alpha('#000', 0.4),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(228, 255, 26, 0.2)',
                    borderColor: '#E4FF1A',
                  }
                }}
                onClick={handleCopyrightClick}
              >
                <CardContent sx={{ py: 4 }}>
                  <ReportProblemIcon sx={{ fontSize: 48, color: '#E4FF1A', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Copyright Claim
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Report copyright infringement
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: alpha('#000', 0.4),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(228, 255, 26, 0.2)',
                    borderColor: '#E4FF1A',
                  }
                }}
                onClick={handleDeleteClick}
              >
                <CardContent sx={{ py: 4 }}>
                  <DeleteForeverIcon sx={{ fontSize: 48, color: '#E4FF1A', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Delete My Data
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Request data deletion
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  backgroundColor: alpha('#000', 0.4),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(228, 255, 26, 0.2)',
                    borderColor: '#E4FF1A',
                  }
                }}
                onClick={() => window.open('https://stats.uptimerobot.com/kgQXzDDkaO', '_blank', 'noopener,noreferrer')}
              >
                <CardContent sx={{ py: 4 }}>
                  <MonitorHeartIcon sx={{ fontSize: 48, color: '#E4FF1A', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Status Page
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Check service status
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* FAQ Section */}
          <Box id="faq-section" sx={{ mb: 6, scrollMarginTop: '80px' }}>
            <SupportFAQ />
          </Box>

          {/* Copyright Claim Form */}
          <Box id="copyright-form" sx={{ mb: 4, scrollMarginTop: '80px' }}>
            <Collapse in={showCopyrightForm}>
              <Card
                sx={{
                  borderRadius: '16px',
                  backgroundColor: alpha('#000', 0.5),
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(228, 255, 26, 0.3)',
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      Copyright Infringement Claim
                    </Typography>
                    <Button 
                      onClick={() => setShowCopyrightForm(false)}
                      sx={{ color: '#E4FF1A' }}
                    >
                      Close
                    </Button>
                  </Box>
                  <CopyrightClaimForm />
                </CardContent>
              </Card>
            </Collapse>
          </Box>

          {/* Delete Data Form */}
          <Box id="delete-form" sx={{ mb: 4, scrollMarginTop: '80px' }}>
            <Collapse in={showDeleteForm}>
              <Card
                sx={{
                  borderRadius: '16px',
                  backgroundColor: alpha('#000', 0.5),
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(228, 255, 26, 0.3)',
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      Request Data Deletion
                    </Typography>
                    <Button 
                      onClick={() => setShowDeleteForm(false)}
                      sx={{ color: '#E4FF1A' }}
                    >
                      Close
                    </Button>
                  </Box>
                  <DeleteDataForm />
                </CardContent>
              </Card>
            </Collapse>
          </Box>

          {/* Contact Support Form */}
          <Box id="contact-form" sx={{ mb: 4, scrollMarginTop: '80px' }}>
            <Collapse in={showContactForm}>
              <Card
                sx={{
                  borderRadius: '16px',
                  backgroundColor: alpha('#000', 0.5),
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(228, 255, 26, 0.3)',
                }}
              >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      Contact Support
                    </Typography>
                    <Button 
                      onClick={() => setShowContactForm(false)}
                      sx={{ color: '#E4FF1A' }}
                    >
                      Close
                    </Button>
                  </Box>
                  <ContactSupportForm />
                </CardContent>
              </Card>
            </Collapse>
          </Box>

          {/* Footer Links */}
          <Box sx={{ textAlign: 'center', mt: 8, pt: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
              Need more help?{' '}
              <Box 
                component="span" 
                onClick={handleContactClick}
                sx={{ 
                  color: '#E4FF1A', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Contact Support
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box 
                component="a" 
                href="/privacy-policy" 
                sx={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', '&:hover': { color: '#E4FF1A' } }}
              >
                Privacy Policy
              </Box>
              <Box 
                component="a" 
                href="/terms-of-service" 
                sx={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none', '&:hover': { color: '#E4FF1A' } }}
              >
                Terms of Service
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SupportPage;

